import React from 'react';
import type { ReactNode } from 'react';
import { graphql } from 'react-relay';

import { expVal } from '@confluence/feature-experiments';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { isRovoSystemSpaceKey } from '@confluence/route-manager/entry-points/isRovoSystemSpaceKey.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	SpaceOverviewSwitcherQuery$data,
	SpaceOverviewSwitcherQuery$variables,
} from './__generated__/SpaceOverviewSwitcherQuery.graphql.ts';
import { SpaceOverview } from './SpaceOverview.tsx';
import { SmartSpaceOverview } from './smart-space-overview/SmartSpaceOverview.tsx';

export const SpaceOverviewSwitcherQuery = graphql`
	query SpaceOverviewSwitcherQuery($cloudId: ID!, $spaceKey: String!) {
		confluence_smartSpaceOverview(cloudId: $cloudId, spaceKey: $spaceKey) {
			summary {
				body
			}
			faq {
				entries {
					question
					answer
					followUpQuestions
					sources {
						content {
							id
							title
							excerpt
							embeddedProduct
							links {
								webui
							}
						}
					}
				}
			}
		}
	}
`;

type SpaceOverviewSwitcherProps = {
	spaceKey: string;
	children?: ReactNode;
};

export const SpaceOverviewSwitcher = ({ spaceKey, children }: SpaceOverviewSwitcherProps) => {
	const { isRovoEnabled, cloudId } = useSessionData();

	const isSmartSpaceOverviewEnabled =
		!isCompanyHubSpaceKey(spaceKey) &&
		!isRovoSystemSpaceKey(spaceKey) &&
		isRovoEnabled &&
		expVal('smart_space_overview', 'isEnabled', false);

	const { data, loading } = useQueryInterop<
		SpaceOverviewSwitcherQuery$data,
		SpaceOverviewSwitcherQuery$variables
	>(SpaceOverviewSwitcherQuery, {
		variables: {
			cloudId,
			spaceKey,
		},
		skip: !isSmartSpaceOverviewEnabled || !cloudId,
	});

	if (isSmartSpaceOverviewEnabled) {
		if (loading) {
			return null;
		}

		const smartSpaceOverviewData = data?.confluence_smartSpaceOverview;
		const hasSummary = !!smartSpaceOverviewData?.summary?.body;

		if (hasSummary) {
			return (
				<SmartSpaceOverview spaceKey={spaceKey} smartSpaceOverviewData={smartSpaceOverviewData} />
			);
		}
	}

	return <SpaceOverview spaceKey={spaceKey}>{children}</SpaceOverview>;
};
